// What a workspace's document screen has besides ui-core's (through the
// list's home, `part.tsx`): the space in its header and the space's items in
// its "…".
//
// Promise:
// - With more than one space, the header names the document's space after the
//   title (Topic's form) and opens the space's overview; a new document's
//   header names the space it will be made in and lets it be changed.
// - "Move to another space" shows to those who may write it when there is a
//   space to move it to (one where they may write documents). "Pin to this
//   space" / "Unpin" shows to those who may change the space's pins.
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { m } from "@trameria/cloud-i18n";
import { m as core } from "@trameria/i18n";
import {
  apiErrorMessage,
  apiErrorWithLead,
  Button,
  type DocumentMenuAddition,
  ErrorLine,
  Icon,
  MenuButton,
  Modal,
  type PageDTO,
  SelectMenu,
  useApi,
} from "@trameria/ui-core";
import { useState } from "react";
import { overviewQuery, usePin } from "../conversations/queries";
import { useCan, useWorkspace } from "../workspace";
import { useMoveWsDocument, wsPlaceQuery } from "./queries";
import {
  useDocumentSpaces,
  useNewDocumentSpace,
  useWritableSpaces,
} from "./spaces";

const titleOf = (page: { title: string }) =>
  page.title || core.documents_untitled();

/** After the title: the document's space, or the space a new one is made in. */
export function DocumentSpace({
  page,
  narrow,
}: {
  page: PageDTO | null;
  narrow: boolean;
}) {
  // A new document has no title yet: where it goes shows at any width.
  if (!page) return <NewDocumentSpace />;
  // Narrow, the title comes first (Topic's header does the same).
  return narrow ? null : <SpaceOf page={page} />;
}

function SpaceOf({ page }: { page: PageDTO }) {
  const { slug } = useWorkspace();
  const spaces = useDocumentSpaces();
  const place = useQuery({
    ...wsPlaceQuery(useApi(), slug, page.id),
    enabled: page.ownerUserId === null,
  });
  const space = place.data?.space;
  if (!space || spaces.length < 2) return null;
  return (
    <Link
      to={`/${slug}/spaces/${space.slug}`}
      className="min-w-0 flex-none truncate text-[12px] text-faint hover:text-body"
    >
      {space.name}
    </Link>
  );
}

function NewDocumentSpace() {
  const target = useNewDocumentSpace();
  if (!target.space || target.spaces.length < 2) return null;
  // Said what it is: alone, a space's name at the header's start read as
  // where one was, not where the new document goes.
  return (
    <span className="flex min-w-0 items-center gap-1.5 text-[12px] text-faint">
      <span className="flex-none">{m.wd_new_in()}</span>
      <NewSpaceMenu target={target} />
    </span>
  );
}

function NewSpaceMenu({
  target,
}: {
  target: ReturnType<typeof useNewDocumentSpace>;
}) {
  if (!target.space) return null;
  return (
    <MenuButton
      label={m.wd_new_in()}
      className="inline-flex min-w-0 flex-none cursor-pointer items-center gap-1 text-[12px] text-muted hover:text-body"
      items={target.spaces.map((s) => ({
        id: s.id,
        label: s.name,
        onSelect: () => target.pick(s.id),
      }))}
    >
      <span className="max-w-[200px] truncate">{target.space.name}</span>
      <Icon kind="updown" size={12} />
    </MenuButton>
  );
}

/** The "…" items of a workspace's document: move it, pin it. */
export function useWorkspaceDocumentMenu(
  page: PageDTO,
  writable: boolean,
): DocumentMenuAddition {
  const { slug } = useWorkspace();
  const can = useCan();
  const api = useApi();
  const shared = page.ownerUserId === null;
  const place = useQuery({
    ...wsPlaceQuery(api, slug, page.id),
    enabled: shared,
  });
  const spaceId = place.data?.space.id ?? null;
  const space = useDocumentSpaces().find((s) => s.id === spaceId) ?? null;
  const targets = useWritableSpaces().filter((s) => s.id !== spaceId);
  const canPin =
    spaceId !== null && (can("workspace.manage") || space?.canManage === true);
  const overview = useQuery({
    ...overviewQuery(api, slug, spaceId ?? ""),
    enabled: canPin,
  });
  const pin = usePin(slug, spaceId ?? "");
  const [moving, setMoving] = useState(false);
  const pinned =
    overview.data?.pins.some(
      (p) => p.kind === "page" && p.targetId === page.id,
    ) ?? false;
  const items: DocumentMenuAddition["items"] = [];
  if (shared && writable && place.data && targets.length > 0)
    items.push({
      id: "move-space",
      label: m.cv_move(),
      onSelect: () => setMoving(true),
    });
  if (canPin && overview.isSuccess)
    items.push({
      id: "pin",
      label: pinned ? m.cv_unpin() : m.cv_pin(),
      onSelect: () =>
        pin.mutate({
          kind: "page",
          targetId: page.id,
          on: !pinned,
          page: {
            id: page.id,
            key: page.key,
            title: page.title,
            kind: page.kind,
          },
        }),
    });
  return {
    items,
    dialogs: (
      <>
        {pin.isError && (
          // Right under the "…" it was chosen from, floating so the header
          // keeps its height (as the document's own menu's failures).
          <span className="relative w-0 flex-none self-stretch">
            <span className="absolute top-full right-0 z-10 mt-1 w-max max-w-[320px] rounded-[7px] border border-border bg-surface px-2.5 pb-1 shadow-[var(--shadow-popover)]">
              <ErrorLine>
                {apiErrorWithLead(
                  pin.error,
                  m.wd_pin_failed({ title: titleOf(page) }),
                )}
              </ErrorLine>
            </span>
          </span>
        )}
        {moving && place.data && (
          <MoveDocument
            page={page}
            from={place.data.space.id}
            onClose={() => setMoving(false)}
          />
        )}
      </>
    ),
  };
}

function MoveDocument({
  page,
  from,
  onClose,
}: {
  page: PageDTO;
  from: string;
  onClose(): void;
}) {
  const { slug } = useWorkspace();
  const targets = useWritableSpaces().filter((s) => s.id !== from);
  const [spaceId, setSpaceId] = useState(targets[0]?.id ?? "");
  const move = useMoveWsDocument(slug, page.id);
  const target = targets.find((s) => s.id === spaceId);
  const title = titleOf(page);
  const save = () => {
    if (!target) return;
    move.mutate(
      { space: { id: target.id, slug: target.slug, name: target.name } },
      { onSuccess: onClose },
    );
  };
  return (
    <Modal open onClose={onClose} label={m.wd_move_title({ title })}>
      <h2 className="text-[15px] font-normal text-fg">
        {m.wd_move_title({ title })}
      </h2>
      <div className="mt-3">
        <SelectMenu
          label={m.cv_space()}
          value={spaceId}
          options={targets.map((s) => ({ value: s.id, label: s.name }))}
          onChange={setSpaceId}
        />
      </div>
      <p className="mt-2 text-[12px] leading-[1.7] text-faint">
        {m.wd_move_hint()}
      </p>
      {move.isError && (
        <ErrorLine>
          {apiErrorMessage(move.error, m.wd_move_failed({ title }))}
        </ErrorLine>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          {m.cv_cancel()}
        </Button>
        <Button
          variant="primary"
          disabled={move.isPending || !target}
          onClick={save}
        >
          {m.cv_move_action()}
        </Button>
      </div>
    </Modal>
  );
}
